/**
 * 配送依頼カード（ShipmentOrderFormCard）が読み書きするフィールドの共通スキーマ。
 *
 * カードは1枚に統合済みのため、カードが扱う入力項目の定義とバリデーション規則もここ1箇所に置く。
 * 各フォーム（作成 / 未配車編集 / 枠＋依頼作成 / 矢羽根編集）は、この entries を spread して
 * 自分固有のフィールドを足し、`withShipmentOrderCardChecks` で共通規則を適用する。
 *
 * `object()` ではなく entries を公開しているのは、矢羽根編集フォームがカード相当の束を
 * root 直下ではなく `shipments` 配列の要素として持つため。
 */

import { isSameDay } from 'date-fns'
import {
  array,
  type BaseIssue,
  type BaseSchema,
  boolean,
  date,
  forward,
  isoTimestamp,
  literal,
  maxLength,
  minLength,
  nullable,
  number,
  object,
  optional,
  partialCheck,
  picklist,
  pipe,
  string,
  transform,
  union
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { safeParseISO } from '@/libs/utils'
import { nonNullableDateToStringSchema } from '@/libs/valibot'

import {
  RoundTypePicklist,
  ShipmentOrderAdditionalFormValueSchema
} from './shipment-order-additional-form-value'
import { shipmentOrderCargoHandlingFormValueFields } from './shipment-order-cargo-handling-form-value'

/** カスタムキー1〜5 の共通定義（拠点マスタで isVisible 指定時のみ入力欄を表示） */
const customKeySchema = nullable(
  pipe(string(), maxLength(30, VALIDATION_MESSAGES.MAX_LENGTH(30)))
)

/** 到着時間制限（発地・着地の開始/終了）の共通定義 - ISO形式 */
const timeRestrictionSchema = nullable(pipe(string(), isoTimestamp()))

/** 地点ごとの作業内容（積込 / 荷降し / その他）。API の WorkType enum と同じ値 */
export const WorkTypePicklist = picklist(['LOADING', 'UNLOADING', 'OTHER'])

/** 経由地の作業内容。未指定（undefined / null）は BE が既定値（その他）を置くため必須にしない */
const workTypeSchema = optional(nullable(WorkTypePicklist))

/**
 * 経由地の到着予定時刻（#5548）。Date と ISO 文字列の両方を受けて ISO 文字列を返す。
 *
 * 配車板の作成モーダルは resolver を通った後の値（日付は ISO 文字列に変換済み）を
 * 送信前にもう一度 parse するため、Date しか受けない共有の `nullableDateToStringSchema`
 * だと 2 回目の parse で落ちる。ルート直下の日付が `nonNullableDateToStringSchema` で
 * `union([date(), string()])` にしているのと同じ考え方で、この項目だけ両方を受ける。
 * 共有スキーマは契約・運行など他の画面でも使うので変えない。
 * 文字列は ISO timestamp（時間制限 `timeRestrictionSchema` と同じ）と、未入力扱いの
 * 空文字だけを受ける。任意の文字列を通すと不正な値がそのまま API へ流れるため
 */
const entryTimeSchema = optional(
  pipe(
    nullable(union([date(), literal(''), pipe(string(), isoTimestamp())])),
    transform((v) =>
      v instanceof Date
        ? v.toISOString()
        : typeof v === 'string' && v
          ? v
          : null
    ),
    nullable(string())
  )
)

/** 配送明細1行のうち、作成系・更新系で共通の項目 */
const itemBaseEntries = {
  /** 品目コード */
  itemCode: nullable(string()),
  /** 品目名 */
  itemName: string(),
  /** 荷姿単位数量 */
  packagedLoadQuantity: nullable(number()),
  /** 荷姿数 */
  packageCount: nullable(number()),
  /** 個数 */
  quantity: nullable(number()),
  /** 重量(t) */
  weight: nullable(number())
}

/**
 * カード契約（ShipmentOrderCardFormValues）と 1:1 のフィールド束。
 * 必須・任意の差は各フォーム側で上書きするため、ここでは緩い側で定義する。
 */
export const shipmentOrderCardEntries = {
  /** 配送依頼No。更新系は `updateOnlyEntries` が必須版で上書きする */
  orderNo: string(),
  /** 荷主ID */
  customerId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 指定パートナーID。矢羽根編集では持たない（読取専用表示） */
  partnerId: nullable(number()),
  /** 配送種別 */
  shipmentCategoryId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 発地地点 */
  originSiteId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 着地地点 */
  destSiteId: number(VALIDATION_MESSAGES.REQUIRED_ITEM),
  /** 発地備考（任意）。地点選択時に地点マスタ Site.note を初期補完する */
  originNote: nullable(string()),
  /** 着地備考（任意） */
  destNote: nullable(string()),
  /**
   * 地点の絞り込みに使ったエリア（#4733、任意）。保存後に選択を画面へ戻すための入力来歴で、
   * 地点の所属エリアとの整合は取らない
   */
  originAreaId: optional(nullable(number())),
  destAreaId: optional(nullable(number())),
  /** 経由地（発地→着地間、訪問順、0件可）。各行の Site.id と備考。未選択行は送信時に除外する */
  waypoints: array(
    object({
      siteId: nullable(number()),
      note: nullable(string()),
      // #4733: 地点の絞り込みに使ったエリア（任意）
      areaId: optional(nullable(number())),
      // 経由地での作業内容（#4813）
      workType: workTypeSchema,
      // 到着予定時刻（#5548）。配車板のモーダルだけが入力欄を持ち、未配車のフォームでは
      // 値を持たない（undefined）ため任意。範囲・並び順の検証は矢羽根の時刻を知る
      // モーダル側の送信処理で行う
      entryTime: entryTimeSchema
    })
  ),
  /** 発送日 */
  shippingDate: nonNullableDateToStringSchema,
  /** 着日 */
  deliveryDate: nonNullableDateToStringSchema,
  /** 請求計上日 */
  incomeRecordingDate: nonNullableDateToStringSchema,
  /** 備考 */
  note: string(),
  /** カスタムキー1 */
  customKey1: customKeySchema,
  /** カスタムキー2 */
  customKey2: customKeySchema,
  /** カスタムキー3 */
  customKey3: customKeySchema,
  /** カスタムキー4 */
  customKey4: customKeySchema,
  /** カスタムキー5 */
  customKey5: customKeySchema,
  /** 荷姿ID */
  packageCategoryId: nullable(number()),
  /** 請求運賃(円) */
  income: nullable(number()),
  /** 貰い運賃(income)の税区分マスタ ID。null のときは BE 側でカスケード fallback */
  incomeTaxCategoryId: nullable(number()),
  /** 貰い運賃(income)の内税フラグ。true のみ内税（税込）、false/null は外税 */
  incomeIsTaxInclusive: nullable(boolean()),
  /** 貰い運賃(income)の端数処理。null のときは BE 側でカスケード fallback */
  incomeRoundType: nullable(RoundTypePicklist),
  /** 発地の到着時間制限(開始) */
  departureTimeRestrictionStart: timeRestrictionSchema,
  /** 発地の到着時間制限(終了) */
  departureTimeRestrictionEnd: timeRestrictionSchema,
  /** 着地の到着時間制限(開始) */
  arrivalTimeRestrictionStart: timeRestrictionSchema,
  /** 着地の到着時間制限(終了) */
  arrivalTimeRestrictionEnd: timeRestrictionSchema,
  /** 荷役作業の委託有無と予定作業時間（積込み・取卸し） */
  ...shipmentOrderCargoHandlingFormValueFields,
  /** 配送明細。更新系は `updateOnlyEntries` が detailId 付きで上書きする */
  items: array(object(itemBaseEntries)),
  /** 車種 */
  truckModelId: nullable(number()),
  /** 請求側付帯費用 (対 荷主、配送依頼単位) */
  additionalCharges: array(ShipmentOrderAdditionalFormValueSchema),
  /** この内容を入力履歴に登録・更新するか */
  saveHistory: boolean(),
  /** 上書き対象の履歴ID */
  historyId: nullable(number()),
  /** 新規登録時の履歴名称 */
  historyName: nullable(string()),
  /**
   * 履歴の保存モード（overwrite: 上書き / new: 新規登録）。
   * 未選択は `undefined`。
   */
  saveMode: optional(picklist(['overwrite', 'new']))
}

/**
 * 更新系（未配車編集 / 矢羽根編集）だけが持つフィールドと、必須へ引き上げる上書き。
 *
 * `id` と `items[].detailId` は「どのレコードを更新するか」の識別子なので作成系には無い。
 * `orderNo` は作成時は採番前で空を許すが、更新時は既存値があるため必須にする。
 */
export const updateOnlyEntries = {
  /** 配送依頼ID。矢羽根編集は複数依頼を1リクエストで更新するため body 側に必要 */
  id: number(),
  /** 配送依頼No（更新系は必須） */
  orderNo: pipe(string(), minLength(1, VALIDATION_MESSAGES.REQUIRED_ITEM)),
  /** 配送明細（更新系は明細IDを持つ。既存行は明細の PK、新規追加行は -1） */
  items: array(object({ detailId: number(), ...itemBaseEntries })),
  /** 作成時に起点となった履歴ID（履歴保存セクションの分岐判定に使う読取専用ミラー） */
  originHistoryId: nullable(number())
}

type RoundType = 'ROUND_UP' | 'ROUND_DOWN' | 'ROUND_HALF_UP'

/** 時間制限の規則が参照するフィールドの最小形 */
type TimeRestrictionInput = {
  shippingDate: string
  deliveryDate: string
  departureTimeRestrictionStart: string | null
  departureTimeRestrictionEnd: string | null
  arrivalTimeRestrictionStart: string | null
  arrivalTimeRestrictionEnd: string | null
}

/** 請求側の税区分・端数処理の必須規則が参照するフィールドの最小形 */
type IncomeTaxInput = {
  incomeTaxCategoryId: number | null
  incomeRoundType: RoundType | null
}

/** 払い側の税区分・端数処理の必須規則が参照するフィールドの最小形 */
type ExpenseTaxInput = {
  expenseTaxCategoryId: number | null
  expenseRoundType: RoundType | null
}

type SchemaOf<TInput> = BaseSchema<unknown, TInput, BaseIssue<unknown>>

/**
 * 規則を適用しても出力型は変わらないため、呼び出し元のスキーマ型をそのまま返す。
 *
 * valibot の `partialCheck` はパス指定を `PathKeys<TInput>` で検査するが、`TInput` が
 * 未解決のジェネリクスだとパスを解決できない。そのため規則本体は最小形の具体型に対して
 * 組み立て、戻り値だけ呼び出し元の型へ戻す。キャストをこの1関数に閉じ込めるための形。
 */
const keepSchemaType = <TSchema>(schema: unknown): TSchema => schema as TSchema

/**
 * 発日と着日が同じ日かどうか。
 * 時間制限の「同日規則」はこの判定が真のときだけ適用し、日跨ぎでは無条件に通す。
 */
const isSameDayDelivery = (
  shippingDate: string,
  deliveryDate: string
): boolean => {
  const shipping = safeParseISO(shippingDate)
  const delivery = safeParseISO(deliveryDate)
  if (!shipping || !delivery) return false
  return isSameDay(shipping, delivery)
}

/**
 * 発着の到着時間制限の規則。
 *
 * 時間制限は「時刻(HH:mm)」であって日時ではない（入力は HH:mm のピッカー、消費側も
 * 時刻部分だけを見る）。そのため時刻同士の大小比較は同一日内の枠として扱い、
 * 発地・着地をまたぐ比較は発日と着日が同じ場合にだけ行う。
 *
 * 規則:
 *   1. 発地の時間制限は開始・終了の両方が必要（片方だけの入力を許さない）
 *   2. 着地の時間制限は開始・終了の両方が必要
 *   3. 発地の終了は開始以降
 *   4. 着地の終了は開始以降
 *   5. 同日のとき、発地の開始は着地の開始より前（同時刻は不可）
 *   6. 同日のとき、発地の終了は着地の開始以降にできない（同時刻は可）
 *   7. 同日のとき、着地の終了は発地の終了以降（同時刻は可）
 */
const applyTimeRestrictionChecks = (schema: SchemaOf<TimeRestrictionInput>) =>
  pipe(
    schema,
    // 1. 発地：開始だけ入力されている
    forward(
      partialCheck(
        [['departureTimeRestrictionStart'], ['departureTimeRestrictionEnd']],
        ({ departureTimeRestrictionStart, departureTimeRestrictionEnd }) =>
          !departureTimeRestrictionStart || !!departureTimeRestrictionEnd,
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_BOTH_REQUIRED
      ),
      ['departureTimeRestrictionEnd']
    ),
    // 1. 発地：終了だけ入力されている
    forward(
      partialCheck(
        [['departureTimeRestrictionStart'], ['departureTimeRestrictionEnd']],
        ({ departureTimeRestrictionStart, departureTimeRestrictionEnd }) =>
          !departureTimeRestrictionEnd || !!departureTimeRestrictionStart,
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_BOTH_REQUIRED
      ),
      ['departureTimeRestrictionStart']
    ),
    // 2. 着地：開始だけ入力されている
    forward(
      partialCheck(
        [['arrivalTimeRestrictionStart'], ['arrivalTimeRestrictionEnd']],
        ({ arrivalTimeRestrictionStart, arrivalTimeRestrictionEnd }) =>
          !arrivalTimeRestrictionStart || !!arrivalTimeRestrictionEnd,
        VALIDATION_MESSAGES.ARRIVAL_TIME_RESTRICTION_BOTH_REQUIRED
      ),
      ['arrivalTimeRestrictionEnd']
    ),
    // 2. 着地：終了だけ入力されている
    forward(
      partialCheck(
        [['arrivalTimeRestrictionStart'], ['arrivalTimeRestrictionEnd']],
        ({ arrivalTimeRestrictionStart, arrivalTimeRestrictionEnd }) =>
          !arrivalTimeRestrictionEnd || !!arrivalTimeRestrictionStart,
        VALIDATION_MESSAGES.ARRIVAL_TIME_RESTRICTION_BOTH_REQUIRED
      ),
      ['arrivalTimeRestrictionStart']
    ),
    // 3. 発地の終了は開始以降
    forward(
      partialCheck(
        [['departureTimeRestrictionStart'], ['departureTimeRestrictionEnd']],
        ({ departureTimeRestrictionStart, departureTimeRestrictionEnd }) => {
          const start = safeParseISO(departureTimeRestrictionStart)
          const end = safeParseISO(departureTimeRestrictionEnd)
          return !start || !end || end >= start
        },
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_START_SHOULD_BE_BEFORE_DEPARTURE_TIME_RESTRICTION_END
      ),
      ['departureTimeRestrictionEnd']
    ),
    // 4. 着地の終了は開始以降
    forward(
      partialCheck(
        [['arrivalTimeRestrictionStart'], ['arrivalTimeRestrictionEnd']],
        ({ arrivalTimeRestrictionStart, arrivalTimeRestrictionEnd }) => {
          const start = safeParseISO(arrivalTimeRestrictionStart)
          const end = safeParseISO(arrivalTimeRestrictionEnd)
          return !start || !end || end >= start
        },
        VALIDATION_MESSAGES.ARRIVAL_TIME_RESTRICTION_START_SHOULD_BE_BEFORE_ARRIVAL_TIME_RESTRICTION_END
      ),
      ['arrivalTimeRestrictionEnd']
    ),
    // 5. 同日：発地の開始は着地の開始より前（同時刻は不可）
    forward(
      partialCheck(
        [
          ['shippingDate'],
          ['deliveryDate'],
          ['departureTimeRestrictionStart'],
          ['arrivalTimeRestrictionStart']
        ],
        ({
          shippingDate,
          deliveryDate,
          departureTimeRestrictionStart,
          arrivalTimeRestrictionStart
        }) => {
          if (!isSameDayDelivery(shippingDate, deliveryDate)) return true
          const departure = safeParseISO(departureTimeRestrictionStart)
          const arrival = safeParseISO(arrivalTimeRestrictionStart)
          return !departure || !arrival || departure < arrival
        },
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_START_SHOULD_BE_BEFORE_ARRIVAL_TIME_RESTRICTION_START_IN_SAME_DAY
      ),
      ['departureTimeRestrictionStart']
    ),
    // 6. 同日：発地の終了は着地の開始以降にできない（同時刻は可）
    forward(
      partialCheck(
        [
          ['shippingDate'],
          ['deliveryDate'],
          ['departureTimeRestrictionEnd'],
          ['arrivalTimeRestrictionStart']
        ],
        ({
          shippingDate,
          deliveryDate,
          departureTimeRestrictionEnd,
          arrivalTimeRestrictionStart
        }) => {
          if (!isSameDayDelivery(shippingDate, deliveryDate)) return true
          const departure = safeParseISO(departureTimeRestrictionEnd)
          const arrival = safeParseISO(arrivalTimeRestrictionStart)
          return !departure || !arrival || departure <= arrival
        },
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_SHOULD_BE_OUT_OF_ARRIVAL_TIME_RESTRICTION
      ),
      ['departureTimeRestrictionEnd']
    ),
    // 7. 同日：着地の終了は発地の終了以降（同時刻は可）
    forward(
      partialCheck(
        [
          ['shippingDate'],
          ['deliveryDate'],
          ['departureTimeRestrictionEnd'],
          ['arrivalTimeRestrictionEnd']
        ],
        ({
          shippingDate,
          deliveryDate,
          departureTimeRestrictionEnd,
          arrivalTimeRestrictionEnd
        }) => {
          if (!isSameDayDelivery(shippingDate, deliveryDate)) return true
          const departure = safeParseISO(departureTimeRestrictionEnd)
          const arrival = safeParseISO(arrivalTimeRestrictionEnd)
          return !departure || !arrival || arrival >= departure
        },
        VALIDATION_MESSAGES.DEPARTURE_TIME_RESTRICTION_END_SHOULD_BE_BEFORE_ARRIVAL_TIME_RESTRICTION_END_IN_SAME_DAY2
      ),
      ['arrivalTimeRestrictionEnd']
    )
  )

export const withTimeRestrictionChecks = <
  TSchema extends SchemaOf<TimeRestrictionInput>
>(
  schema: TSchema
): TSchema => keepSchemaType<TSchema>(applyTimeRestrictionChecks(schema))

const applyIncomeTaxRequiredChecks = (schema: SchemaOf<IncomeTaxInput>) =>
  pipe(
    schema,
    forward(
      partialCheck(
        [['incomeTaxCategoryId']],
        ({ incomeTaxCategoryId }) => incomeTaxCategoryId != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['incomeTaxCategoryId']
    ),
    forward(
      partialCheck(
        [['incomeRoundType']],
        ({ incomeRoundType }) => incomeRoundType != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['incomeRoundType']
    )
  )

/**
 * 請求側（貰い運賃）の税区分・端数処理を必須にする。
 * 金額の有無に関わらず必須。内税フラグは対象外（null=外税仕様）。
 *
 * フィールド型は DB の null データを read して populate するため nullable のまま、
 * 保存時バリデーションで必須化する。
 */
export const withIncomeTaxRequiredChecks = <
  TSchema extends SchemaOf<IncomeTaxInput>
>(
  schema: TSchema
): TSchema => keepSchemaType<TSchema>(applyIncomeTaxRequiredChecks(schema))

const applyExpenseTaxRequiredChecks = (schema: SchemaOf<ExpenseTaxInput>) =>
  pipe(
    schema,
    forward(
      partialCheck(
        [['expenseTaxCategoryId']],
        ({ expenseTaxCategoryId }) => expenseTaxCategoryId != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['expenseTaxCategoryId']
    ),
    forward(
      partialCheck(
        [['expenseRoundType']],
        ({ expenseRoundType }) => expenseRoundType != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['expenseRoundType']
    )
  )

/**
 * 払い側（払い運賃）の税区分・端数処理を必須にする。
 * 請求側と同方針。内税フラグは対象外（null=外税仕様）。
 */
export const withExpenseTaxRequiredChecks = <
  TSchema extends SchemaOf<ExpenseTaxInput>
>(
  schema: TSchema
): TSchema => keepSchemaType<TSchema>(applyExpenseTaxRequiredChecks(schema))
